import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Bot, Wallet, ArrowLeftRight, CheckSquare,
   ScrollText, BarChart3, ShieldCheck, Settings, Menu, X, LogOut,
  Power, Bell, Inbox, Mail, PenLine, Briefcase,
} from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { DemoBadge } from "@/components/common";
import { toast } from "sonner";

const nav = [
  { key: "overview", to: "/app", icon: LayoutDashboard, end: true },
  { key: "agents", to: "/app/agents", icon: Bot },
  { key: "wallets", to: "/app/wallets", icon: Wallet },
  { key: "transactions", to: "/app/transactions", icon: ArrowLeftRight },
  { key: "approvals", to: "/app/approvals", icon: CheckSquare },
  { key: "policies", to: "/app/policies", icon: ScrollText },
  { key: "analytics", to: "/app/analytics", icon: BarChart3 },
  { key: "security", to: "/app/security", icon: ShieldCheck },
  { key: "settings", to: "/app/settings", icon: Settings },
];

function NavList({ onNavigate, isAdmin }) {
  const { t } = useLanguage();
  const items = isAdmin
    ? [...nav.slice(0, 5), { key: "waitlist", to: "/app/waitlist", icon: Inbox }, { key: "leads", to: "/app/leads", icon: Mail }, { key: "content", to: "/app/content", icon: PenLine }, { key: "careers", to: "/app/careers", icon: Briefcase }, ...nav.slice(5)]
    : nav;
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((item) => {
        const Icon = item.icon;
        const label = t(`dash.${item.key}`);
        return (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? "bg-[#4F8CFF]/12 text-white border border-[#4F8CFF]/25"
                  : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }
            data-testid={`sidebar-nav-${item.key}`}
          >
            <Icon className="h-[18px] w-[18px]" />
            {label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const triggerKillswitch = () => {
    toast.success("Emergency stop activated (demo)", {
      description: "All agents paused and payments disabled across the sandbox.",
    });
  };

  const initials = (user?.name || "V")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-[#05080D] text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/8 bg-[#07111F] fixed inset-y-0 left-0 z-30">
        <div className="h-16 flex items-center px-5 border-b border-white/8">
          <Logo to="/app" />
        </div>
        <div className="flex-1 overflow-y-auto py-5">
          <NavList isAdmin={isAdmin} />
        </div>
        <div className="p-3 border-t border-white/8">
          <DemoBadge className="w-full justify-center" />
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-16 vora-glass border-b border-white/8 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden p-2 rounded-lg hover:bg-white/5" aria-label="Open sidebar" data-testid="dashboard-mobile-trigger">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-[#07111F] border-r border-white/10 p-0">
                <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
                  <Logo to="/app" onClick={() => setMobileOpen(false)} />
                  <button onClick={() => setMobileOpen(false)} className="p-2 text-slate-300" aria-label="Close"><X className="h-5 w-5" /></button>
                </div>
                <div className="py-5">
                  <NavList isAdmin={isAdmin} onNavigate={() => setMobileOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="hidden sm:block">
              <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Control Center</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSwitcher />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  className="bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 rounded-full gap-2"
                  data-testid="emergency-killswitch-button"
                >
                  <Power className="h-4 w-4" />
                  <span className="hidden sm:inline">Emergency Stop</span>
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0B1526] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Activate emergency stop?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    This pauses every agent, freezes all wallets and disables payments immediately.
                    This is a demo action in the sandbox.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid="killswitch-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={triggerKillswitch} className="bg-red-500 hover:bg-red-600 text-white" data-testid="killswitch-confirm">
                    Stop everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <button className="relative p-2 rounded-lg hover:bg-white/5 text-slate-300" aria-label="Notifications" data-testid="topbar-notifications">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-[#4F8CFF]" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-white/5" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-gradient-to-br from-[#4F8CFF] to-[#7C5CFF] text-white text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0B1526] border-white/10 text-white">
                <DropdownMenuLabel className="font-normal">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/app/settings")} className="focus:bg-white/5 cursor-pointer" data-testid="menu-settings">
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/")} className="focus:bg-white/5 cursor-pointer" data-testid="menu-home">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Marketing site
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={handleLogout} className="focus:bg-white/5 cursor-pointer text-red-400" data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <div key={location.pathname} className="vora-fade-up p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
